import React, { forwardRef, useCallback, useRef } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useModalFocusLifecycle } from '../../hooks/useOverlayFocusLifecycle';

// Types
export type BadgeVariant =
  | 'primary'
  | 'neutral'
  | 'accent'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';
export type BadgeSize = 'sm' | 'md' | 'lg';
export type ButtonVariant = 'primary' | 'secondary' | 'neutral' | 'error' | 'success' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type AlertVariant = 'info' | 'success' | 'warning' | 'error';
export type SpinnerSize = 'sm' | 'md' | 'lg';
export type SpinnerColor = 'primary' | 'white';
export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';
export type AvatarStatus = 'online' | 'offline' | 'away';

// Props interfaces
export interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  isLoading?: boolean;
  className?: string;
}

export interface AlertProps {
  children: React.ReactNode;
  variant?: AlertVariant;
  icon?: React.ReactNode;
  title?: string;
  onClose?: () => void;
  className?: string;
}

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  withPattern?: boolean;
  withGradient?: boolean;
  variant?: 'default' | 'interactive';
  style?: React.CSSProperties;
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  icon?: React.ReactNode;
  label?: string;
  className?: string;
}

export interface FormControlProps {
  children: React.ReactNode;
  error?: string;
  className?: string;
}

export interface FormLabelProps {
  children: React.ReactNode;
  required?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  error?: string;
  helperText?: string;
  className?: string;
}

export interface SpinnerProps {
  size?: SpinnerSize;
  color?: SpinnerColor;
  className?: string;
}

export interface AvatarProps {
  src?: string;
  name: string;
  size?: AvatarSize;
  status?: AvatarStatus;
  className?: string;
}

export interface DividerProps {
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  children?: React.ReactNode;
  label?: string;
}

export interface CircuitPatternProps {
  opacity?: number;
}

export interface SectionProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  dialogRole?: 'dialog' | 'alertdialog';
  className?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  returnFocusRef?: React.RefObject<HTMLElement>;
  placement?: 'center' | 'bottom-responsive' | 'right';
  overlayClassName?: string;
  style?: React.CSSProperties;
  overlayTestId?: string;
  dialogTestId?: string;
}

type ModalAccessibleNameProps =
  | {
      ariaLabel: string;
      ariaLabelledBy?: never;
    }
  | {
      ariaLabel?: never;
      ariaLabelledBy: string;
    };

export type ModalProps = ModalBaseProps & ModalAccessibleNameProps;

// Components
export const Badge = ({
  children,
  variant = 'neutral',
  size = 'md',
  className = '',
}: BadgeProps) => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-md border font-medium leading-tight';

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[11px]',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm',
  };

  const variantClasses = {
    primary: 'border-ratuj-300/30 bg-ratuj-300/10 text-ratuj-200',
    neutral: 'border-primary-dark-300/40 bg-primary-dark-100/20 text-primary-light-300',
    accent: 'border-gold-300/30 bg-gold-300/10 text-gold-300',
    error: 'border-status-error-500/35 bg-status-error-500/10 text-status-error-400',
    success: 'border-status-success-500/32 bg-status-success-500/10 text-status-success-400',
    warning: 'border-status-warning-500/32 bg-status-warning-500/10 text-status-warning-400',
    info: 'border-status-info-500/30 bg-status-info-500/10 text-status-info-300',
  };

  return (
    <span className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}>
      {children}
    </span>
  );
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      icon,
      iconPosition = 'left',
      fullWidth = false,
      isLoading = false,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center rounded-lg font-semibold transition-colors duration-150 focus-outline';

    const sizeClasses = {
      sm: 'min-h-[34px] px-3 py-1.5 text-xs',
      md: 'min-h-[40px] px-4 py-2 text-sm',
      lg: 'min-h-[46px] px-5 py-2.5 text-base',
    };

    const variantClasses = {
      primary: 'bg-ratuj-400 hover:bg-ratuj-300 text-white shadow-button',
      secondary:
        'border border-primary-dark-300/50 bg-primary-dark-100/30 hover:bg-primary-dark-300/50 text-primary-light-100',
      neutral:
        'border border-primary-dark-300/50 bg-primary-dark-100/30 hover:bg-primary-dark-300/50 text-primary-light-100',
      error: 'bg-status-error-500 hover:bg-status-error-400 text-white',
      success: 'bg-status-success-500 hover:bg-status-success-400 text-white',
      accent: 'border border-gold-300/35 bg-gold-300/10 hover:bg-gold-300/20 text-gold-200',
    };

    const disabledClasses = 'opacity-50 cursor-not-allowed';
    const fullWidthClasses = 'w-full';

    return (
      <button
        ref={ref}
        className={`
        ${baseClasses}
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${fullWidth ? fullWidthClasses : ''}
        ${disabled || isLoading ? disabledClasses : ''}
        ${className}
        `}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <Spinner size="sm" color="white" className="mr-2" />
            {children}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
            {children}
            {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export const Card = ({
  children,
  className = '',
  withPattern = false,
  withGradient = false,
  variant = 'default',
}: CardProps) => {
  const baseClasses =
    'bg-primary-dark-200/36 backdrop-blur-sm border border-primary-dark-300/24 rounded-lg';
  const variantClasses = {
    default: 'shadow-none',
    interactive: 'shadow-none transition-colors hover:border-primary-dark-400/38',
  };
  const patternClass = withPattern ? 'bg-pattern-dots' : '';
  const gradientClass = withGradient ? 'bg-gradient-ratuj' : '';

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${patternClass} ${gradientClass} ${className}`}
    >
      {children}
    </div>
  );
};

export const Alert = ({
  children,
  variant = 'info',
  icon,
  title,
  onClose,
  className = '',
}: AlertProps) => {
  const baseClasses = 'flex items-start rounded-lg border p-3 text-sm sm:p-3.5';

  const variantClasses = {
    info: 'bg-status-info-500/10 border-status-info-500/25 text-status-info-300',
    success: 'bg-status-success-500/10 border-status-success-500/25 text-status-success-400',
    warning: 'bg-status-warning-500/10 border-status-warning-500/25 text-status-warning-400',
    error: 'bg-status-error-500/10 border-status-error-500/30 text-status-error-400',
  };

  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {icon && <span className="mr-3 flex-shrink-0">{icon}</span>}
      <div className="flex-1">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="ml-3 flex-shrink-0 hover:opacity-80 transition-opacity"
          aria-label="Zamknij alert"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, icon, label, className = '', ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const isDateInput = props.type === 'date';
    const inputId = typeof props.id === 'string' ? props.id : undefined;
    const { onClick, ...inputProps } = props;

    const openDatePicker = useCallback(() => {
      if (!isDateInput) {
        return;
      }

      const input = inputRef.current;
      if (!input || input.disabled || input.readOnly) {
        return;
      }

      try {
        const dateInput = input as HTMLInputElement & { showPicker?: () => void };
        if (typeof dateInput.showPicker === 'function') {
          dateInput.showPicker();
          return;
        }
      } catch {
        // no-op: fallback below keeps focus behavior on browsers without showPicker
      }

      input.focus();
    }, [isDateInput]);

    const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
      onClick?.(event);
      if (event.defaultPrevented || !isDateInput) {
        return;
      }
      openDatePicker();
    };

    const setInputRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
        return;
      }
      if (ref) {
        ref.current = node;
      }
    };

    return (
      <div className="relative">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-primary-light-200 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {icon}
            </div>
          )}
          <input
            className={`
            w-full min-h-[42px] px-3 py-2 bg-primary-dark-100/60 border border-primary-dark-300/60 rounded-lg 
            text-primary-light-100 placeholder-primary-light-400
            focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            ${icon ? 'pl-10' : ''}
            ${isDateInput ? 'rs-date-input pr-16 [color-scheme:dark]' : ''}
            ${error ? 'border-status-error-500 focus:ring-status-error-500' : ''}
            ${className}
          `}
            ref={setInputRef}
            {...inputProps}
            onClick={handleInputClick}
          />
          {isDateInput && (
            <button
              type="button"
              aria-label="Otwórz kalendarz"
              className="absolute inset-y-1.5 right-2 flex items-center rounded-md border border-primary-dark-300/70 bg-primary-dark-100/85 px-2 text-primary-light-100 shadow-sm transition-colors hover:border-primary-dark-200/80 hover:bg-primary-dark-100 focus:outline-none focus:ring-2 focus:ring-ratuj-300/70"
              data-testid="date-input-action"
              onClick={openDatePicker}
            >
              <CalendarDaysIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-status-error-500">{error}</p>}
      </div>
    );
  }
);

export const FormControl = ({ children, error, className = '' }: FormControlProps) => {
  return (
    <div className={`space-y-1 ${className}`}>
      {children}
      {error && <p className="text-sm text-status-error-500">{error}</p>}
    </div>
  );
};

export const FormLabel = ({ children, required, icon, className = '' }: FormLabelProps) => {
  return (
    <label
      className={`flex items-center text-sm font-medium text-primary-light-200 ${className}`} // Use flex to align icon and text
    >
      {icon && <span className="mr-1.5 flex-shrink-0">{icon}</span>}
      {children}
      {required && <span className="ml-1 text-status-error-500">*</span>}
    </label>
  );
};

export const Radio = ({ label, error, helperText, className = '', ...props }: RadioProps) => {
  return (
    <label className={`flex flex-col ${className}`}>
      <div className="flex items-center">
        <input
          type="radio"
          className={`
            w-4 h-4 text-ratuj-300 bg-primary-dark-200 border-primary-dark-300
            focus:ring-ratuj-300 focus:ring-offset-primary-dark-100
            ${error ? 'border-status-error-500' : ''}
          `}
          {...props}
        />
        <span className="ml-2 text-primary-light-200">{label}</span>
      </div>
      {helperText && (
        <span className="ml-6 mt-0.5 text-sm text-primary-light-400">{helperText}</span>
      )}
    </label>
  );
};

export const Spinner = ({ size = 'md', color = 'primary', className = '' }: SpinnerProps) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  const colorClasses = {
    primary: 'text-ratuj-300',
    white: 'text-white',
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

export const Avatar = ({ src, name, size = 'md', status, className = '' }: AvatarProps) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
  };

  const statusColors = {
    online: 'bg-status-success-500',
    offline: 'bg-status-error-500',
    away: 'bg-status-warning-500',
  };

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative inline-block">
      <div
        className={`
          relative inline-flex items-center justify-center rounded-full bg-ratuj-300
          text-white font-medium ${sizeClasses[size]} ${className}
        `}
      >
        {src ? (
          <img src={src} alt={name} className="w-full h-full object-cover rounded-full" />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      {status && (
        <span
          className={`absolute bottom-0 right-0 block rounded-full ring-2 ring-primary-dark-200
            ${sizeClasses[size].includes('w-8') ? 'w-2.5 h-2.5' : 'w-3 h-3'}
            ${statusColors[status]}
          `}
        />
      )}
    </div>
  );
};

export const Divider = ({
  className = '',
  orientation = 'horizontal',
  children,
  label,
}: DividerProps) => {
  if (orientation === 'vertical') {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="h-full w-px bg-primary-dark-300/70" />
        {(children || label) && <div className="mx-4">{children || label}</div>}
      </div>
    );
  }

  if (children || label) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="flex-1 h-px bg-primary-dark-300/70" />
        <div className="px-4 text-primary-light-400 text-sm">{children || label}</div>
        <div className="flex-1 h-px bg-primary-dark-300/70" />
      </div>
    );
  }

  return <div className={`h-px bg-primary-dark-300/70 ${className}`} />;
};

export const CircuitPattern = ({ opacity = 0.03 }: CircuitPatternProps) => {
  return (
    <svg
      className="absolute inset-0 w-full h-full"
      style={{ opacity }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <pattern
        id="circuit-pattern"
        x="0"
        y="0"
        width="100"
        height="100"
        patternUnits="userSpaceOnUse"
      >
        <path
          d="M50 0v25h25v25h25M0 50h25m25 0h25m25 0h25M25 75v25m50-25v25"
          stroke="currentColor"
          strokeWidth="1"
          fill="none"
        />
      </pattern>
      <rect width="100%" height="100%" fill="url(#circuit-pattern)" />
    </svg>
  );
};

export const Section = ({ children, className = '' }: SectionProps) => {
  return <div className={`mx-auto max-w-6xl ${className}`}>{children}</div>;
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  dialogRole = 'dialog',
  className = '',
  ariaLabel,
  ariaLabelledBy,
  initialFocusRef,
  returnFocusRef,
  placement = 'center',
  overlayClassName = '',
  style,
  overlayTestId = 'modal-overlay',
  dialogTestId,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useModalFocusLifecycle({
    isOpen,
    dialogRef,
    onClose,
    initialFocusRef,
    returnFocusRef,
  });

  if (!isOpen) return null;

  const placementClasses = {
    center: 'items-center justify-center p-3 sm:p-4',
    'bottom-responsive':
      'items-end justify-center pt-[max(0.75rem,env(safe-area-inset-top))] sm:items-center sm:p-4',
    right: 'items-stretch justify-end',
  };

  return (
    <div
      data-testid={overlayTestId}
      className={`fixed inset-0 z-[70] flex overflow-hidden bg-black/62 transition-opacity duration-300 ease-in-out ${placementClasses[placement]} ${overlayClassName}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        data-testid={dialogTestId}
        role={dialogRole}
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        style={style}
        className={`max-h-[calc(100dvh-1.5rem)] min-w-0 w-full max-w-lg overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg border border-primary-dark-300/65 bg-primary-dark-200 shadow-dropdown sm:max-h-[calc(100dvh-2rem)] ${className}`}
      >
        {children}
      </div>
    </div>
  );
};
